import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  PlayIcon,
  PauseIcon,
  ShoppingCartIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  BoltIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import toast from 'react-hot-toast';

function AmazonAutoPanel() {
  const [config, setConfig] = useState({
    asin: '',
    productName: '',
    productUrl: '',
    quantity: 1,
    intervalSeconds: 30
  });
  
  const queryClient = useQueryClient();

  // Obtener estado
  const { data: statusData, isLoading, refetch } = useQuery('amazon-status', async () => {
    const response = await api.get('/amazon/status');
    return response.data;
  });

  // Obtener cookies de Amazon
  const { data: cookiesData } = useQuery('amazon-cookies', async () => {
    const response = await api.get('/amazon/cookies');
    return response.data;
  });

  // Iniciar tarea
  const startMutation = useMutation(
    () => api.post('/amazon/start', config),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('amazon-status');
        toast.success('✅ Tarea iniciada');
        refetch();
      },
      onError: () => toast.error('❌ Error al iniciar tarea')
    }
  );

  // Detener tarea
  const stopMutation = useMutation(
    () => api.post('/amazon/stop'),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('amazon-status');
        toast.success('⏸️ Tarea detenida');
        refetch();
      },
      onError: () => toast.error('❌ Error al detener tarea')
    }
  );

  const status = statusData?.status;
  const cookies = cookiesData?.cookies || [];
  const isRunning = status?.enabled || false;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '1rem', paddingBottom: '6rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ffffff' }}>🛒 Amazon Auto-Add</h1>
        <p style={{ color: '#ff9900' }}>Agrega productos al carrito automáticamente con rotación de cookies</p>
      </div>

      {/* Cookies disponibles */}
      <div style={{ background: '#111111', borderRadius: '1rem', padding: '1rem', marginBottom: '1.5rem', border: '1px solid #222222' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <span style={{ color: '#9ca3af' }}>🍪 Cookies :</span>
            <span style={{ color: '#ff9900', fontWeight: 'bold', marginLeft: '0.5rem' }}>{cookies.length}</span>
            {status && (
              <>
                <span style={{ color: '#9ca3af', marginLeft: '1rem' }}>✅ Éxitos: {status.stats?.successful || 0}</span>
                <span style={{ color: '#ff0033', marginLeft: '1rem' }}>❌ Fallos: {status.stats?.failed || 0}</span>
                <span style={{ color: '#9ca3af', marginLeft: '1rem' }}>🚫 Bloqueadas: {status.blockedCookies || 0}</span>
              </>
            )}
          </div>
          <div>
            {isRunning ? (
              <button
                onClick={() => stopMutation.mutate()}
                style={{ padding: '0.75rem 1.5rem', background: '#ff0033', border: 'none', borderRadius: '0.5rem', color: '#ffffff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <PauseIcon style={{ width: '1.25rem', height: '1.25rem' }} />
                Detener
              </button>
            ) : (
              <button
                onClick={() => startMutation.mutate()}
                disabled={cookies.length === 0}
                style={{ padding: '0.75rem 1.5rem', background: cookies.length === 0 ? '#333333' : 'linear-gradient(135deg, #ff9900, #ff6600)', border: 'none', borderRadius: '0.5rem', color: '#000000', fontWeight: 'bold', cursor: cookies.length === 0 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: cookies.length === 0 ? 0.5 : 1 }}
              >
                <PlayIcon style={{ width: '1.25rem', height: '1.25rem' }} />
                Iniciar Auto-Add
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Configuración */}
      <div style={{ background: '#111111', borderRadius: '1rem', padding: '1.5rem', marginBottom: '1.5rem', border: '1px solid #222222' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#ff9900', marginBottom: '1rem' }}>⚙️ Configuración del Producto</h2>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          <input
            type="text"
            placeholder="ASIN (ej: B08N5WRWNW)"
            value={config.asin}
            onChange={(e) => setConfig({...config, asin: e.target.value})}
            style={{ padding: '0.75rem', background: '#1a1a1a', border: '1px solid #333333', borderRadius: '0.5rem', color: '#ffffff' }}
            disabled={isRunning}
          />
          <input
            type="text"
            placeholder="Nombre del producto"
            value={config.productName}
            onChange={(e) => setConfig({...config, productName: e.target.value})}
            style={{ padding: '0.75rem', background: '#1a1a1a', border: '1px solid #333333', borderRadius: '0.5rem', color: '#ffffff' }}
            disabled={isRunning}
          />
          <input
            type="text"
            placeholder="URL del producto (opcional)"
            value={config.productUrl}
            onChange={(e) => setConfig({...config, productUrl: e.target.value})}
            style={{ gridColumn: 'span 2', padding: '0.75rem', background: '#1a1a1a', border: '1px solid #333333', borderRadius: '0.5rem', color: '#ffffff' }}
            disabled={isRunning}
          />
          <input
            type="number"
            placeholder="Cantidad"
            value={config.quantity}
            onChange={(e) => setConfig({...config, quantity: parseInt(e.target.value)})}
            style={{ padding: '0.75rem', background: '#1a1a1a', border: '1px solid #333333', borderRadius: '0.5rem', color: '#ffffff' }}
            disabled={isRunning}
          />
          <input
            type="number"
            placeholder="Intervalo (30-45 segundos)"
            value={config.intervalSeconds}
            onChange={(e) => setConfig({...config, intervalSeconds: parseInt(e.target.value)})}
            style={{ padding: '0.75rem', background: '#1a1a1a', border: '1px solid #333333', borderRadius: '0.5rem', color: '#ffffff' }}
            disabled={isRunning}
          />
        </div>
      </div>

      {/* Estado actual */}
      {status && isRunning && (
        <div style={{ background: '#111111', borderRadius: '1rem', padding: '1rem', marginBottom: '1.5rem', border: '1px solid #ff9900' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <BoltIcon style={{ width: '1.25rem', height: '1.25rem', color: '#ff9900' }} />
            <span style={{ color: '#ff9900', fontWeight: 'bold' }}>Ejecutando...</span>
          </div>
          <div style={{ fontSize: '0.875rem', color: '#9ca3af' }}>
            Producto: {status.productName}<br/>
            Intervalo: cada {status.intervalSeconds} segundos<br/>
            Intentos: {status.stats?.totalAttempts || 0} | ✅ Éxitos: {status.stats?.successful || 0} | ❌ Fallos: {status.stats?.failed || 0}
          </div>
          {status.nextRunAt && (
            <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.5rem' }}>
              ⏰ Próximo intento: {new Date(status.nextRunAt).toLocaleTimeString()}
            </div>
          )}
        </div>
      )}

      {/* Historial */}
      <div style={{ background: '#111111', borderRadius: '1rem', border: '1px solid #222222', overflow: 'hidden' }}>
        <div style={{ padding: '1rem', borderBottom: '1px solid #222222', background: '#1a1a1a' }}>
          <h3 style={{ color: '#ff9900', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <ClockIcon style={{ width: '1.25rem', height: '1.25rem' }} />
            Historial de Intentos
          </h3>
        </div>
        
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {!status?.recentAttempts?.length ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>
              <ShoppingCartIcon style={{ width: '3rem', height: '3rem', margin: '0 auto 1rem', opacity: 0.5 }} />
              <p>No hay actividad aún</p>
              <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>Inicia el proceso para ver resultados</p>
            </div>
          ) : (
            status.recentAttempts.map((attempt, index) => (
              <div key={index} style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #222222', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div>
                  {attempt.status === 'success' ? (
                    <CheckCircleIcon style={{ width: '1.25rem', height: '1.25rem', color: '#ccff00' }} />
                  ) : (
                    <XCircleIcon style={{ width: '1.25rem', height: '1.25rem', color: '#ff0033' }} />
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <span style={{ color: '#ffffff' }}>{attempt.productName}</span>
                    <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>{new Date(attempt.createdAt).toLocaleTimeString()}</span>
                  </div>
                  <div style={{ fontSize: '0.7rem', color: attempt.status === 'success' ? '#ccff00' : '#ff0033', marginTop: '0.25rem' }}>
                    {attempt.message}
                  </div>
                  {attempt.cookieService && (
                    <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '0.25rem' }}>
                      🍪 {attempt.cookieService}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default AmazonAutoPanel;